"use client";

import * as React from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

export interface MonitoringTrendPoint {
  date: string; // ISO
  overall: number | null;
  performance?: number | null;
  quality?: number | null;
  trust?: number | null;
  dr?: number | null;
}

export type MonitoringMetricKey =
  | "overall"
  | "performance"
  | "quality"
  | "trust"
  | "dr";

export const MONITORING_METRICS: Record<
  MonitoringMetricKey,
  { label: string; stroke: string; fill: string }
> = {
  overall: {
    label: "Overall",
    stroke: "url(#stroke-overall)",
    fill: "url(#fill-overall)",
  },
  performance: {
    label: "Performance",
    stroke: "url(#stroke-perf)",
    fill: "url(#fill-perf)",
  },
  quality: {
    label: "Quality",
    stroke: "url(#stroke-quality)",
    fill: "url(#fill-quality)",
  },
  trust: {
    label: "Trust & Security",
    stroke: "url(#stroke-trust)",
    fill: "url(#fill-trust)",
  },
  dr: {
    label: "Domain Rating",
    stroke: "url(#stroke-dr)",
    fill: "url(#fill-dr)",
  },
};

interface MonitoringTrendChartProps {
  data: MonitoringTrendPoint[];
  className?: string;
  showHeader?: boolean;
  metric?: MonitoringMetricKey;
  onMetricChange?: (metric: MonitoringMetricKey) => void;
}

function formatDateLabel(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function FullDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

interface TooltipPayloadItem {
  payload: {
    date: string;
    value: number | null;
    originalValue?: number | null;
  };
}

function CustomTooltip({
  active,
  payload,
  metric,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  metric: MonitoringMetricKey;
}) {
  if (!active || !payload || !payload[0]) return null;
  const p = payload[0].payload;
  const displayValue =
    p.originalValue !== undefined ? p.originalValue : p.value;
  return (
    <div className="rounded-xl border border-border/50 bg-background/95 px-3 py-2 shadow-sm">
      <div className="text-xs text-muted-foreground">{FullDate(p.date)}</div>
      <div className="text-sm font-semibold">
        {MONITORING_METRICS[metric].label}: {displayValue ?? "–"}
      </div>
    </div>
  );
}

export function MonitoringTrendChart({
  data,
  className = "",
  showHeader = false,
  metric,
  onMetricChange,
}: MonitoringTrendChartProps) {
  const isControlled = metric !== undefined;
  const [internalMetric, setInternalMetric] =
    React.useState<MonitoringMetricKey>(metric ?? "overall");
  const activeMetric = metric ?? internalMetric;

  const handleMetricChange = (next: MonitoringMetricKey) => {
    if (!isControlled) {
      setInternalMetric(next);
    }
    onMetricChange?.(next);
  };

  const prepared = React.useMemo(() => {
    const mapped = data.map((d) => ({
      date: d.date,
      label: formatDateLabel(d.date),
      value: (d[activeMetric] as number | null) ?? null,
      originalValue: (d[activeMetric] as number | null) ?? null,
    }));

    // Check if all values are the same (causes line not to render)
    const values = mapped.map((d) => d.value).filter((v) => v !== null);
    const allSame = values.length > 1 && values.every((v) => v === values[0]);

    if (allSame && values.length > 1) {
      // Add tiny variations to make the line render
      return mapped.map((d, index) => ({
        ...d,
        value: d.value !== null ? d.value + index * 0.001 : d.value,
      }));
    }

    return mapped;
  }, [data, activeMetric]);

  return (
    <div className={className}>
      {showHeader && (
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold">Score Trend</h3>
          <div className="hidden sm:inline-flex items-center gap-1 rounded-full border border-border/50 bg-muted/20 p-0.5 text-[11px]">
            {(Object.keys(MONITORING_METRICS) as MonitoringMetricKey[]).map(
              (key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleMetricChange(key)}
                  className={
                    "rounded-full px-2.5 py-1 transition-colors " +
                    (activeMetric === key
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground")
                  }
                  aria-pressed={activeMetric === key}
                >
                  {MONITORING_METRICS[key].label}
                </button>
              )
            )}
          </div>
        </div>
      )}
      <div className="h-64 w-full sm:h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            key={activeMetric}
            data={prepared}
            margin={{ top: 6, right: 6, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="stroke-overall" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="#93c5fd" />
                <stop offset="100%" stopColor="#3b82f6" />
              </linearGradient>
              <linearGradient id="stroke-perf" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="#86efac" />
                <stop offset="100%" stopColor="#22c55e" />
              </linearGradient>
              <linearGradient id="stroke-quality" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="#fcd34d" />
                <stop offset="100%" stopColor="#f59e0b" />
              </linearGradient>
              <linearGradient id="stroke-trust" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="#fca5a5" />
                <stop offset="100%" stopColor="#ef4444" />
              </linearGradient>
              <linearGradient id="stroke-dr" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="#c4b5fd" />
                <stop offset="100%" stopColor="#8b5cf6" />
              </linearGradient>

              <linearGradient id="fill-overall" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#1d4ed8" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="fill-perf" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#22c55e" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#166534" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="fill-quality" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#b45309" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="fill-trust" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#ef4444" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#991b1b" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="fill-dr" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#5b21b6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              vertical={false}
              strokeDasharray="3 3"
              stroke="rgba(255,255,255,0.06)"
            />
            <XAxis
              dataKey="label"
              tick={{ fill: "var(--muted-foreground)" }}
              tickMargin={8}
              interval="preserveStartEnd"
              minTickGap={24}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fill: "var(--muted-foreground)" }}
              tickCount={5}
              width={32}
              tickFormatter={(v) => String(v)}
            />
            <Tooltip content={<CustomTooltip metric={activeMetric} />} />
            <Area
              type="monotone"
              dataKey="value"
              stroke="none"
              fill={MONITORING_METRICS[activeMetric].fill}
              isAnimationActive
              animationDuration={1200}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={MONITORING_METRICS[activeMetric].stroke}
              strokeWidth={2.5}
              dot={false}
              strokeLinecap="round"
              isAnimationActive
              animationDuration={1400}
              connectNulls={false}
              activeDot={{ r: 0 }}
              strokeDasharray="0"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
